import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  InputLabel,
  CircularProgress,
  Typography,
} from '@mui/material'
import { RenderField } from '../../components/Field'
import { useParams } from 'react-router-dom'
import {
  useGetEmailExists,
  useGetIdNumberExists,
} from '../user/enrollment-steps/hooks'
import debounce from 'lodash.debounce'
import { useAuth0 } from '@auth0/auth0-react'

type UserInfoPropTypes = {
  saveUser: (data: any) => void
  userData: any | object
  id?: any
  disabled: boolean
  userPhotoRaw: any
}

export const UserFormNew = (props: UserInfoPropTypes) => {
  const { saveUser, userData, disabled, userPhotoRaw } = props
  const { id } = useParams()
  const [formData, setFormData] = useState<any | undefined>(userData)
  const [workerEmail, setWorkerEmail] = useState<any | undefined>(null)
  const [workerIdNumber, setWorkerIdNumber] = useState<any | undefined>(null)
  const [preventSave, setPreventSave] = useState<boolean>(false)
  const { data: emailExists, isFetching: isEmailExistsFetching } =
    useGetEmailExists('email', workerEmail)
  const originalEmail = (userData as { email?: string })?.email
  const { data: idNumberExists, isFetching: isIdNumberExistsFetching } =
    useGetIdNumberExists('idNumber', workerIdNumber)
  const originalIdNumber = (userData as { idNumber?: number })?.idNumber
  const [authUser, setAuthUser] = useState<any | undefined>(null)
  const { user } = useAuth0()
  const [userRoles, setUserRoles] = useState<any[]>([])

  useEffect(() => {
    if (user) {
      setAuthUser(user)
      setUserRoles(user[`${process.env.REACT_APP_WEB_URL}/roles`])
    }
  }, [user])

  const fields: any[] = [
    {
      fieldType: 'input',
      name: 'email',
      label: 'Email',
      helperText: 'john@email.com',
      errorMessage: 'Oops, error with email',
      rules: null,
    },
    {
      fieldType: 'input',
      name: 'firstName',
      label: 'First Name',
      helperText: 'Juan',
      errorMessage: 'Oops, error with first name.',
      isRequired: true,
      rules: null,
    },
    {
      fieldType: 'input',
      name: 'lastName',
      label: 'Last Name',
      helperText: 'Garcia',
      errorMessage: 'Oops, error with last name.',
      isRequired: true,
      rules: null,
    },
    {
      fieldType: 'input',
      name: 'idNumber',
      label: 'ID Number',
      helperText: '',
      errorMessage: 'Oops, error with ID Number.',
      isRequired: true,
      rules: null,
    },
    {
      fieldType: 'singleselect',
      name: 'type',
      label: 'User Role',
      helperText: '',
      errorMessage: 'Oops, error with User Role',
      isRequired: true,
      rules: null,
      options: [
        { value: 'superintendent', label: 'Superintendent' },
        { value: 'vtadmin', label: 'VT Admin' },
        { value: 'worker', label: 'Worker' },
      ],
      disabled: userRoles.includes('superintendent') ? true : false,
    },
  ]

  const handleSearchingEmail = useCallback(
    debounce((email: string) => {
      setWorkerEmail(email)
    }, 500),
    []
  )

  const handleSearchingId = useCallback(
    debounce((id: string) => {
      setWorkerIdNumber(id.trim())
    }, 500),
    []
  )

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (email === '' || emailRegex.test(email)) {
      //setPreventSave(false)
      return true
    } else {
      setPreventSave(true)
      return false
    }
  }

  const onDataEntry = (e: any) => {
    const { name, value } = e.target
    console.log('name--------->', name)
    let trimmedValue = value.trim()
    if (name === 'firstName') {
      trimmedValue = trimmedValue.substring(0, 15)
    }
    if (name === 'lastName') {
      trimmedValue = trimmedValue.substring(0, 15)
    }
    setFormData((formData: any) => ({ ...formData, [name]: trimmedValue }))
    if (name === 'idNumber') {
      trimmedValue = trimmedValue.substring(0, 15)
    }
    setFormData((formData: any) => ({ ...formData, [name]: trimmedValue }))

    // Call handleSearchingEmail here
    if (name === 'email' && validateEmail(value) && value !== originalEmail) {
      handleSearchingEmail(value)
    }
    if (name === 'idNumber' && value.trim() !== originalIdNumber) {
      handleSearchingId(value)
    }
  }

  const handleSave = () => {
    saveUser(formData)
  }

  useEffect(() => {
    if (emailExists?.exists || idNumberExists?.exists) {
      setPreventSave(true)
    } else if (!emailExists?.exists || !idNumberExists?.exists) {
      setPreventSave(false)
    }
    // if formData is an empty object, then set preventSave to true
    if (Object.keys(formData).length === 0) {
      setPreventSave(true)
    }
    // if formData has empty required fields, then set preventSave to true
    fields.forEach(field => {
      if (field.isRequired && !formData[field.name]) {
        setPreventSave(true)
      }
    })
    if (!userPhotoRaw && formData.type !== 'vtadmin') {
      setPreventSave(true)
    }
    // if email is present in formData, but invalid, then set preventSave to true
    if (formData.email && !validateEmail(formData.email)) {
      setPreventSave(true)
    }
    // If the user type is not a worker, require an email.
    if (formData.type !== 'worker' && !formData.email) {
      setPreventSave(true)
    }
  }, [emailExists, formData, userPhotoRaw, userData])

  return (
    <>
      <form>
        {fields.map((field, key) => (
          <Box key={key} className='formfield'>
            <InputLabel sx={{ display: 'block', fontWeight: 'bold' }}>
              {field.label}
            </InputLabel>
            {(field.name === 'email' && isEmailExistsFetching) ||
              (field.name === 'idNumber' && isIdNumberExistsFetching && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.5rem',
                  }}>
                  <CircularProgress size='1rem' />
                  <div style={{ marginLeft: '0.5rem' }}>
                    Verifying {field.name}...
                  </div>
                </div>
              ))}
            {field.name === 'email' && emailExists?.exists && (
              <div style={{ color: 'red' }}>
                This email is already registered
              </div>
            )}
            {RenderField(field, key, onDataEntry, formData[field.name])}
            {field.name === 'idNumber' &&
              idNumberExists?.exists &&
              formData[field.name] !== originalIdNumber && (
                <Typography sx={{ color: 'red' }}>
                  This ID Number is already registered
                </Typography>
              )}
          </Box>
        ))}
        {!userPhotoRaw && formData.type !== 'vtadmin' && preventSave && (
          <Typography sx={{ color: 'red', mb: 2 }}>
            * A photo is required
          </Typography>
        )}
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={
            disabled ||
            isEmailExistsFetching ||
            emailExists?.exists ||
            preventSave
          }>
          {!id && !userData?._id ? 'Enroll User' : 'Update User'}
        </Button>
      </form>
    </>
  )
}
